import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  TextField,
  DatePicker,
  SelectField,
} from '@flash-tecnologia/hros-web-ui-v2';
import { InputLayout } from 'components/Layout/InputLayout';
import { RowContainer } from 'components/Containers';
import { RadioButton } from 'components/RadioButtom';
import { TitleAndSubtitle } from 'components/TitleAndSubtitle';
import { useIndividualShareholder } from '../useIndividualShareholder';
import { useCorporateShareholder } from '../useCorporateShareholder';
import { cpfMask, cnpjMask } from 'utils/masks';
import { countryOptions } from 'utils/countries';

interface ShareholderFormItemProps {
  index: number;
  initialData?: any;
  onDataChange?: (data: any) => void;
}

export interface ShareholderFormItemRef {
  triggerValidation: () => boolean;
  getData: () => { type: 'individual' | 'corporate' | null; data: any } | null;
  hasSelectedType: () => boolean;
}

export const ShareholderFormItem = forwardRef<
  ShareholderFormItemRef,
  ShareholderFormItemProps
>(({ index, initialData, onDataChange }, ref) => {
  const [shareholderTypeError, setShareholderTypeError] = useState<
    string | null
  >(null);
  const [selectedShareholderType, setSelectedShareholderType] = useState<
    'individual' | 'corporate' | null
  >(null);

  const [
    individualShareholderData,
    individualShareholderStateFns,
    validateIndividualShareholder,
    individualShareholderErrors,
  ] = useIndividualShareholder();

  const [
    corporateShareholderData,
    corporateShareholderStateFns,
    validateCorporateShareholder,
    corporateShareholderErrors,
  ] = useCorporateShareholder();

  useImperativeHandle(ref, () => ({
    triggerValidation: () => {
      if (!selectedShareholderType) {
        setShareholderTypeError(
          'Selecione o tipo de pessoa (Física ou Jurídica).',
        );
        return false;
      }
      setShareholderTypeError(null);
      if (selectedShareholderType === 'individual')
        return validateIndividualShareholder();
      if (selectedShareholderType === 'corporate')
        return validateCorporateShareholder();
      return false;
    },
    getData: () => {
      if (!selectedShareholderType) return null;
      return {
        type: selectedShareholderType,
        data:
          selectedShareholderType === 'individual'
            ? individualShareholderData
            : corporateShareholderData,
      };
    },
    hasSelectedType: () => !!selectedShareholderType,
  }));

  const handleTypeChange = (type: 'individual' | 'corporate') => {
    if (selectedShareholderType !== type) {
      if (selectedShareholderType === 'individual')
        individualShareholderStateFns.resetState();
      if (selectedShareholderType === 'corporate')
        corporateShareholderStateFns.resetState();
      setSelectedShareholderType(type);
      setShareholderTypeError(null);
    }
  };

  useEffect(() => {
    if (initialData) {
      if (initialData.type === 'individual') {
        setSelectedShareholderType('individual');
        individualShareholderStateFns.setAll(initialData.data);
      } else if (initialData.type === 'corporate') {
        setSelectedShareholderType('corporate');
        corporateShareholderStateFns.setAll(initialData.data);
      }
    }
  }, [initialData]);

  return (
    <InputLayout title={`Informações da pessoa ${index + 1}`}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '20px',
          marginBottom: '20px',
          marginTop: '10px',
          width: '100%',
        }}
      >
        <div
          style={{
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '10px',
            borderColor: 'rgba(235, 230, 233, 1)',
            borderWidth: '1px',
            width: '100%',
            height: '3.375em',
          }}
        >
          <RadioButton
            value="individual"
            label="Pessoa Física"
            checked={selectedShareholderType === 'individual'}
            onChange={() => {
              handleTypeChange('individual');
              if (onDataChange) {
                onDataChange({
                  type: 'individual',
                  data: individualShareholderData,
                });
              }
            }}
          />
        </div>
        <div
          style={{
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '10px',
            borderColor: 'rgba(235, 230, 233, 1)',
            borderWidth: '1px',
            width: '100%',
            height: '3.375em',
          }}
        >
          <RadioButton
            value="corporate"
            label="Pessoa Jurídica"
            checked={selectedShareholderType === 'corporate'}
            onChange={() => {
              handleTypeChange('corporate');
              if (onDataChange) {
                onDataChange({
                  type: 'corporate',
                  data: corporateShareholderData,
                });
              }
            }}
          />
        </div>
      </div>
      {shareholderTypeError && (
        <p
          style={{ color: 'orange', marginTop: '-10px', marginBottom: '10px' }}
        >
          {shareholderTypeError}
        </p>
      )}

      {selectedShareholderType === 'individual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          <div>
            <TextField
              fullWidth
              size="large"
              onChange={(e) => {
                individualShareholderStateFns.setName(e.currentTarget.value);
                if (onDataChange) {
                  onDataChange({
                    type: 'individual',
                    data: {
                      ...individualShareholderData,
                      name: e.currentTarget.value,
                    },
                  });
                }
              }}
              value={individualShareholderData.name}
              label="Nome Completo"
              error={!!individualShareholderErrors.nameError}
              helperText={individualShareholderErrors.nameError}
            />
          </div>
          <RowContainer style={{ gap: '25px' }}>
            <div style={{ width: '50%' }}>
              <TextField
                fullWidth
                size="large"
                onChange={(e) => {
                  individualShareholderStateFns.setDocument(
                    cpfMask(e.target.value),
                  );
                  if (onDataChange) {
                    onDataChange({
                      type: 'individual',
                      data: {
                        ...individualShareholderData,
                        document: cpfMask(e.target.value),
                      },
                    });
                  }
                }}
                value={individualShareholderData.document}
                label="CPF"
                error={!!individualShareholderErrors.documentError}
                helperText={individualShareholderErrors.documentError}
              />
            </div>
            <div style={{ width: '50%' }}>
              <TextField
                fullWidth
                size="large"
                type="number"
                onChange={(e) => {
                  individualShareholderStateFns.setParticipation(
                    Number(e.currentTarget.value) || 0,
                  );

                  if (onDataChange) {
                    onDataChange({
                      type: 'individual',
                      data: {
                        ...individualShareholderData,
                        participation: Number(e.currentTarget.value) || 0,
                      },
                    });
                  }
                }}
                value={individualShareholderData.participation || ''}
                label="Participação (%)"
                error={!!individualShareholderErrors.participationError}
                helperText={individualShareholderErrors.participationError}
              />
            </div>
          </RowContainer>
          <RowContainer style={{ gap: '25px' }}>
            <div style={{ width: '50%' }}>
              <DatePicker
                fullWidth
                size="large"
                label="Data de Nascimento"
                value={individualShareholderData.birthDate}
                onDateChange={(e) => {
                  individualShareholderStateFns.setBirthdate(
                    e?.format('YYYY-MM-DD') ?? '',
                  );
                  if (onDataChange) {
                    onDataChange({
                      type: 'individual',
                      data: {
                        ...individualShareholderData,
                        birthDate: e?.format('YYYY-MM-DD') ?? '',
                      },
                    });
                  }
                }}
                error={!!individualShareholderErrors.birthDateError}
                helperText={individualShareholderErrors.birthDateError}
              />
            </div>
            <div style={{ width: '50%' }}>
              <TextField
                fullWidth
                size="large"
                onChange={(e) => {
                  individualShareholderStateFns.setPosition(
                    e.currentTarget.value,
                  );
                  if (onDataChange) {
                    onDataChange({
                      type: 'individual',
                      data: {
                        ...individualShareholderData,
                        position: e.currentTarget.value,
                      },
                    });
                  }
                }}
                value={individualShareholderData.position}
                label="Cargo"
                error={!!individualShareholderErrors.positionError}
                helperText={individualShareholderErrors.positionError}
              />
            </div>
          </RowContainer>
          <div>
            <TextField
              fullWidth
              size="large"
              onChange={(e) => {
                individualShareholderStateFns.setAddress(e.currentTarget.value);
                if (onDataChange) {
                  onDataChange({
                    type: 'individual',
                    data: {
                      ...individualShareholderData,
                      address: e.currentTarget.value,
                    },
                  });
                }
              }}
              value={individualShareholderData.address}
              label="Endereço"
              error={!!individualShareholderErrors.addressError}
              helperText={individualShareholderErrors.addressError}
            />
          </div>
          <TitleAndSubtitle
            title="Pessoa Exposta Politicamente (PEP)?"
            subTitle="Informe se a pessoa ocupa ou já ocupou nos últimos 5 anos cargo púbico relevante ou tem ligação próxima à pessoas nesta condição."
          />
          <RowContainer style={{ marginLeft: '-10px' }}>
            <RadioButton
              label="Sim, ocupa/ocupou"
              value={individualShareholderData.isPEP}
              checked={individualShareholderData.isPEP}
              onChange={() => {
                individualShareholderStateFns.setIsPEP(true);
                if (onDataChange) {
                  onDataChange({
                    type: 'individual',
                    data: {
                      ...individualShareholderData,
                      isPEP: true,
                    },
                  });
                }
              }}
            />
            <RadioButton
              label="Nunca ocupou"
              checked={!individualShareholderData.isPEP}
              value={!individualShareholderData.isPEP}
              onChange={() => {
                individualShareholderStateFns.setIsPEP(false);
                if (onDataChange) {
                  onDataChange({
                    type: 'individual',
                    data: {
                      ...individualShareholderData,
                      isPEP: false,
                    },
                  });
                }
              }}
            />
          </RowContainer>
        </div>
      )}

      {selectedShareholderType === 'corporate' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          <div>
            <TextField
              fullWidth
              size="large"
              onChange={(e) => {
                corporateShareholderStateFns.setLegalName(
                  e.currentTarget.value,
                );
                if (onDataChange) {
                  onDataChange({
                    type: 'corporate',
                    data: {
                      ...corporateShareholderData,
                      legalName: e.currentTarget.value,
                    },
                  });
                }
              }}
              value={corporateShareholderData.legalName}
              label="Razão Social"
              error={!!corporateShareholderErrors.legalNameError}
              helperText={corporateShareholderErrors.legalNameError}
            />
          </div>
          <RowContainer style={{ gap: '25px' }}>
            <div style={{ width: '50%' }}>
              <TextField
                fullWidth
                size="large"
                onChange={(e) => {
                  corporateShareholderStateFns.setDocument(
                    cnpjMask(e.target.value),
                  );
                  if (onDataChange) {
                    onDataChange({
                      type: 'corporate',
                      data: {
                        ...corporateShareholderData,
                        document: cnpjMask(e.target.value),
                      },
                    });
                  }
                }}
                value={corporateShareholderData.document}
                label="CNPJ"
                error={!!corporateShareholderErrors.documentError}
                helperText={corporateShareholderErrors.documentError}
              />
            </div>
            <div style={{ width: '50%' }}>
              <TextField
                fullWidth
                size="large"
                type="number"
                onChange={(e) => {
                  corporateShareholderStateFns.setParticipation(
                    Number(e.currentTarget.value) || 0,
                  );
                  if (onDataChange) {
                    onDataChange({
                      type: 'corporate',
                      data: {
                        ...corporateShareholderData,
                        participation: Number(e.currentTarget.value) || 0,
                      },
                    });
                  }
                }}
                value={corporateShareholderData.participation || ''}
                label="Participação (%)"
                error={!!corporateShareholderErrors.participationError}
                helperText={corporateShareholderErrors.participationError}
              />
            </div>
          </RowContainer>
          <RowContainer style={{ gap: '25px' }}>
            <div style={{ width: '25%' }}>
              <SelectField
                fullWidth
                searchable
                InputProps={{
                  style: {
                    borderColor: 'rgba(235, 230, 233, 1)',
                    borderWidth: '1px',
                  },
                }}
                label="País"
                value={corporateShareholderData.country}
                options={countryOptions}
                onSelectChange={(_, option) => {
                  corporateShareholderStateFns.setCountry(
                    option.value as string,
                  );
                  if (onDataChange) {
                    onDataChange({
                      type: 'corporate',
                      data: {
                        ...corporateShareholderData,
                        country: option.value as string,
                      },
                    });
                  }
                }}
                error={!!corporateShareholderErrors.countryError}
                helperText={corporateShareholderErrors.countryError}
              />
            </div>
            <div style={{ width: '100%' }}>
              <TextField
                fullWidth
                size="large"
                onChange={(e) => {
                  corporateShareholderStateFns.setAddress(
                    e.currentTarget.value,
                  );
                  if (onDataChange) {
                    onDataChange({
                      type: 'corporate',
                      data: {
                        ...corporateShareholderData,
                        address: e.currentTarget.value,
                      },
                    });
                  }
                }}
                value={corporateShareholderData.address}
                label="Endereço Completo"
                error={!!corporateShareholderErrors.addressError}
                helperText={corporateShareholderErrors.addressError}
              />
            </div>
          </RowContainer>
        </div>
      )}
    </InputLayout>
  );
});
